import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IdNameList,
  Locale,
} from "../../../../../../../../../../api/services/common/models";
import { useGetProjectActivityById } from "../../../../../../../../../../api/services/project/projectApis";
import { LANGUAGE_CODE } from "../../../../../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../../../../../helpers/constants/siteCoreKeys";
import {
  convertLocalizeObjToFormValue,
  isNotEmpty,
} from "../../../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { userPreferredLanguageSelector } from "../../../../../../../../../../redux/slices/appState/appStateSelector";
import { toggleToast } from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import {
  EditActivityValueProps,
  ProjectActivitiesRowValue,
  ProjectCreateActivityDefaultFormKeys,
  ProjectCreateActivityGeneralFormKeys,
} from "../../modelsAndSchemas/models";
import { ProjectCreateActivity } from "../createActivity/ProjectCreateActivity";

// Falls back to any available translation when the topic has no entry for the currently selected UI language
const setLocalizedTopicForCurrentLanguage = (
  localizedTopics: Locale[],
  userSelectedLanguage: string,
) => {
  const userSelectedLanguageTopic = localizedTopics.find(
    (item) => item.localeKey === userSelectedLanguage,
  );

  const userSelectedLanguageTopicName = userSelectedLanguageTopic?.name;

  if (isNotEmpty(userSelectedLanguageTopicName)) {
    return localizedTopics;
  }

  const englishLanguageTopic = localizedTopics.find(
    (item) => item.localeKey === LANGUAGE_CODE.EN_US,
  );
  const fallbackTopic = isNotEmpty(englishLanguageTopic?.name)
    ? englishLanguageTopic
    : localizedTopics.find((item) => isNotEmpty(item.name));
  const fallbackTopicName = fallbackTopic?.name;

  if (isNotEmpty(fallbackTopicName)) {
    return [
      ...localizedTopics,
      { name: fallbackTopicName, localeKey: userSelectedLanguage },
    ];
  }

  return localizedTopics;
};

interface ProjectEditActivityProps {
  handleCloseModal: () => void;
  pageId: string;
  visible: boolean;
  selectedProjectActivity: ProjectActivitiesRowValue;
  getListOfProjectActivitiesApi: () => void;
}
export const ProjectEditActivity: FC<ProjectEditActivityProps> = ({
  selectedProjectActivity,
  visible,
  pageId,
  handleCloseModal,
  getListOfProjectActivitiesApi,
}) => {
  const dispatch = useDispatch();
  const { commonLabels } = useSiteCore();
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const [projectActivityValue, setProjectActivityValues] =
    useState<EditActivityValueProps>();
  const {
    data: projectActivityData,
    isFetching: isPendingProjectActivity,
    isError: isErrorProjectActivity,
  } = useGetProjectActivityById(selectedProjectActivity.id);

  useEffect(() => {
    if (isErrorProjectActivity) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isErrorProjectActivity]);

  useEffect(() => {
    if (isNotEmpty(projectActivityData)) {
      const defaultValues = {
        [ProjectCreateActivityGeneralFormKeys.activityType]:
          projectActivityData?.activityType ?? "",
        [ProjectCreateActivityGeneralFormKeys.modality]:
          projectActivityData?.modalityIds ?? "",
        [ProjectCreateActivityGeneralFormKeys.topic]:
          convertLocalizeObjToFormValue(
            setLocalizedTopicForCurrentLanguage(
              projectActivityData.localizedTopics ?? [],
              userSelectedLanguage,
            ),
          ),
        [ProjectCreateActivityGeneralFormKeys.deliverables]:
          projectActivityData?.deliverables?.map((item: IdNameList) => item.id),
        [ProjectCreateActivityGeneralFormKeys.activityStatus]:
          projectActivityData?.activityStatusId ?? "",
        [ProjectCreateActivityGeneralFormKeys.triggerBilling]:
          projectActivityData?.triggerBilling ?? false,
        [ProjectCreateActivityGeneralFormKeys.lastContactDate]:
          projectActivityData?.countsTowardsLastContactDate ?? false,
        [ProjectCreateActivityGeneralFormKeys.triggerTouch]:
          projectActivityData?.triggerTouchPointSurvey ?? false,
        [ProjectCreateActivityDefaultFormKeys.units]:
          projectActivityData.units ?? "",
        [ProjectCreateActivityDefaultFormKeys.measurementMinute]:
          projectActivityData?.units === UnitTypesKey.Minutes
            ? projectActivityData.measurement
            : "",
        [ProjectCreateActivityDefaultFormKeys.measurementOther]:
          projectActivityData?.units === UnitTypesKey.Days ||
          projectActivityData?.units === UnitTypesKey.Count
            ? Number(projectActivityData.measurement)
            : null,
      };
      setProjectActivityValues(defaultValues);
    }
  }, [projectActivityData, userSelectedLanguage]);

  return (
    <ProjectCreateActivity
      visible={visible}
      onClickCloseModal={handleCloseModal}
      pageId={pageId}
      getListOfProjectActivitiesApi={getListOfProjectActivitiesApi}
      isEditProjectActivity={true}
      editActivityData={projectActivityValue}
      isPendingActivity={isPendingProjectActivity}
      selectedProjectActivity={selectedProjectActivity}
    />
  );
};
